// Rocket Ship — a retro cartoon rocket straight off a storybook cover: a plump
// red body with a cream nose cone and cream belly band, round portholes, three
// swept fin legs it stands on, and a pair of orange exhaust flames licking out
// of the nozzle. Built toy-like: fat silhouette, oversized rivets, warm flat
// colours, everything readable at a glance.
//
// Frame: y = 0 is the launch pad (the fin feet touch it), +Z is the side the
// portholes face. About 2.5 m tall.
//
// Animation layout (flat nodes, deliberate pivots):
//   - "hull"  : the whole red body + nose + portholes (not the fins). Sways a
//               touch in idle and rattles on its fin legs when launch-ready.
//               Pivot at the hull base.
//   - "flame" : both exhaust cones under the nozzle. Flicker by scale pulse
//               about the nozzle exit, so the flame lengthens downward.
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

const RED = "#d9534a";        // main body — warm toy red
const RED_DARK = "#b23a31";   // shaded body panels / nose tip
const CREAM = "#f6efe3";      // trim: nose cone, belly band, porthole rings
const CREAM_DARK = "#ded2bb"; // shaded cream (fin undersides)
const GLASS = "#8fd3df";      // porthole glass — friendly sky teal
const NOZZLE = "#5c5350";     // warm dark grey engine bell (never pure black)
const FLAME = "#f2842c";      // outer exhaust flame
const FLAME_CORE = "#f7c948"; // hot inner core

// Frame heights.
const NOZZLE_EXIT = 0.24;                 // bottom lip of the engine bell
const HULL_BASE = 0.46;                   // where the red body starts
const HULL_PIVOT: [number, number, number] = [0, HULL_BASE, 0];
const FLAME_PIVOT: [number, number, number] = [0, NOZZLE_EXIT + 0.04, 0];

// One swept fin leg: a leaning blade that runs from the hull down to a rounded
// foot pad on the ground. Placed by azimuth so the porthole face (+Z) stays
// clear. Static — the rocket stands on these while the hull sways.
function fin(azimuth: number): Part[] {
  const r = 0.46;                          // blade centre distance from axis
  const footR = 0.66;                      // foot pad distance from axis
  const x = Math.sin(azimuth), z = Math.cos(azimuth);
  return [
    // The blade: tall, thin tangentially, deep radially, leaning outward so the
    // bottom edge kicks out to the foot. eulerOrder YXZ = yaw first, then lean.
    {
      geometry: new THREE.BoxGeometry(0.09, 1.15, 0.42),
      color: RED_DARK,
      position: [x * r, 0.62, z * r],
      rotation: [0.32, azimuth, 0],
      eulerOrder: "YXZ",
    },
    // Cream foot pad — a squashed dome that grounds the leg.
    {
      geometry: new THREE.SphereGeometry(0.13, 10, 6),
      color: CREAM_DARK,
      position: [x * footR, 0.03, z * footR],
      scale: [1, 0.45, 1],
    },
  ];
}

// A porthole on the +Z face: cream ring plus teal glass disc, both facing +Z.
function porthole(y: number, z: number, ringR: number): Part[] {
  return [
    { geometry: new THREE.CylinderGeometry(ringR, ringR, 0.06, 12), color: CREAM,
      position: [0, y, z], rotation: [Math.PI / 2, 0, 0], name: "hull" },
    { geometry: new THREE.CylinderGeometry(ringR * 0.68, ringR * 0.68, 0.03, 12), color: GLASS,
      position: [0, y, z + 0.03], rotation: [Math.PI / 2, 0, 0], name: "hull" },
  ];
}

function rocketParts(): Part[] {
  const parts: Part[] = [
    // Fin legs at 60 / 180 / 300 degrees, leaving the +Z porthole face open.
    ...fin(Math.PI / 3),
    ...fin(Math.PI),
    ...fin((5 * Math.PI) / 3),

    // Engine bell: a dark flared cone under the hull.
    { geometry: new THREE.CylinderGeometry(0.20, 0.28, 0.24, 12), color: NOZZLE,
      position: [0, NOZZLE_EXIT + 0.12, 0], name: "hull" },

    // The plump body: two tapered drums meeting at the widest point, with a
    // cream belly band over the seam. Widest at y = 1.15 for the toy look.
    { geometry: new THREE.CylinderGeometry(0.44, 0.33, 0.70, 14), color: RED,
      position: [0, HULL_BASE + 0.35, 0], name: "hull" },
    { geometry: new THREE.CylinderGeometry(0.30, 0.44, 0.72, 14), color: RED,
      position: [0, 1.52, 0], name: "hull" },
    { geometry: new THREE.CylinderGeometry(0.455, 0.455, 0.14, 14), color: CREAM,
      position: [0, 1.16, 0], name: "hull" },

    // Nose cone: cream, with a little red tip ball.
    { geometry: new THREE.ConeGeometry(0.315, 0.62, 14), color: CREAM,
      position: [0, 2.19, 0], name: "hull" },
    { geometry: new THREE.SphereGeometry(0.07, 10, 8), color: RED_DARK,
      position: [0, 2.50, 0], name: "hull" },

    // Portholes on the +Z face: a big captain's window and a small lower one.
    ...porthole(1.42, 0.395, 0.145),
    ...porthole(0.92, 0.415, 0.095),

    // Exhaust flames: two cones pointing down out of the bell (rotated so the
    // tip hangs toward the pad). Named "flame" so they flicker together.
    { geometry: new THREE.ConeGeometry(0.17, 0.26, 10), color: FLAME,
      position: [0, 0.16, 0], rotation: [Math.PI, 0, 0], name: "flame" },
    { geometry: new THREE.ConeGeometry(0.09, 0.18, 8), color: FLAME_CORE,
      position: [0, 0.14, 0], rotation: [Math.PI, 0, 0], name: "flame" },
  ];

  // A ring of oversized cream rivets around the belly band.
  const RIVETS = 6;
  for (let i = 0; i < RIVETS; i++) {
    const a = (i / RIVETS) * Math.PI * 2 + Math.PI / RIVETS;
    parts.push({
      geometry: new THREE.SphereGeometry(0.035, 8, 6),
      color: RED_DARK,
      position: [Math.sin(a) * 0.455, 1.16, Math.cos(a) * 0.455],
      name: "hull",
    });
  }
  return parts;
}

export const rocketShip = defineModel({
  id: "rocket-ship",
  batchId: "mess",
  name: "Rocket Ship",
  tags: ["mess", "cute", "animated", "vehicle", "rocket", "space", "retro", "toy"],
  summary: "A plump retro cartoon rocket standing on three fin legs, flames flickering.",
  description:
    "A storybook launch pad hero: a fat red rocket with a cream nose cone and " +
    "belly band, round teal portholes, oversized rivets and three swept fin legs " +
    "it stands on. Two exhaust cones under the engine bell flicker in idle and " +
    "roar harder in the launch-ready animation, while the hull rattles gently on " +
    "its legs.",
  author: "models",
  footprint: 0.75,
  tiles: { w: 1, h: 1 },
  parts: rocketParts,
  animations: [
    // Idle — the pilot light: the flames breathe softly and the hull sways a
    // whisker on its legs. Always alive, never dramatic.
    {
      id: "idle",
      label: "Idle",
      duration: 2.8,
      loop: true,
      tracks: [
        {
          target: "flame", pivot: FLAME_PIVOT,
          scale: [
            { t: 0, value: [1, 1, 1], ease: "sine" },
            { t: 0.7, value: [0.92, 1.1, 0.92], ease: "sine" },
            { t: 1.4, value: [1.04, 0.9, 1.04], ease: "sine" },
            { t: 2.1, value: [0.95, 1.06, 0.95], ease: "sine" },
            { t: 2.8, value: [1, 1, 1], ease: "sine" },
          ],
        },
        {
          target: "hull", pivot: HULL_PIVOT,
          rotation: [
            { t: 0, value: [0, 0, 0.012], ease: "sine" },
            { t: 1.4, value: [0, 0, -0.012], ease: "sine" },
            { t: 2.8, value: [0, 0, 0.012], ease: "sine" },
          ],
        },
      ],
    },

    // Launch-ready — throttle up: the flames pulse long and fast and the hull
    // rattles on the fin legs. Reads as "hold onto your hat".
    {
      id: "launch-ready",
      label: "Launch ready",
      duration: 1.0,
      loop: true,
      tracks: [
        {
          target: "flame", pivot: FLAME_PIVOT,
          scale: [
            { t: 0, value: [1.05, 1.15, 1.05], ease: "sine" },
            { t: 0.25, value: [0.85, 1.45, 0.85], ease: "sine" },
            { t: 0.5, value: [1.1, 1.05, 1.1], ease: "sine" },
            { t: 0.75, value: [0.9, 1.4, 0.9], ease: "sine" },
            { t: 1.0, value: [1.05, 1.15, 1.05], ease: "sine" },
          ],
        },
        {
          target: "hull", pivot: HULL_PIVOT,
          position: [
            { t: 0, value: [0, 0, 0] },
            { t: 0.2, value: [0.008, 0.004, -0.006] },
            { t: 0.4, value: [-0.007, 0, 0.008] },
            { t: 0.6, value: [0.006, 0.005, 0.006] },
            { t: 0.8, value: [-0.008, 0.002, -0.007] },
            { t: 1.0, value: [0, 0, 0] },
          ],
        },
      ],
    },
  ],
});
